<script setup lang="ts">
import StressTestCard from '@/components/rc/miner-repairs/stress-test/StressTestCard.vue'
import useMinerStressTests, { type UIStressTest } from '@/composables/rc/miner-stress-tests'
import { watchOnce } from '@vueuse/core'
import { computed, reactive, toRefs, watchEffect } from 'vue'

const props = defineProps<{ minerId: number }>()

const open = defineModel<boolean>()
const emit = defineEmits(['close'])

const { minerId } = toRefs(props)
const { getStressTestsQuery, deleteStressTestsMutation } = useMinerStressTests(minerId)

const { isPending: stresTestsQueryPending } = getStressTestsQuery
const { isPending: isDeletePending } = deleteStressTestsMutation

// Force refetch stress tests when modal is opened
// Subsequent refetches are handled by the mutations
watchOnce(open, () => {
  getStressTestsQuery.refetch()
})

const stressTests = reactive<UIStressTest[]>([])

watchEffect(() => {
  if (getStressTestsQuery.data.value) {
    stressTests.splice(0, stressTests.length)
    for (const s of getStressTestsQuery.data.value) {
      stressTests.push({ ...s, id: s.id!, isLatest: s.isLatest, flaggedDelete: false })
    }
  }
})

const amountFlaggedForDeletion = computed(() => stressTests.filter((test) => test.flaggedDelete).length)

const close = () => emit('close')

// UI Actions:

// Reset all flagged deletes and close modal
const cancel = () => {
  stressTests.forEach((test) => (test.flaggedDelete = false))
  close()
}

// Delete all flagged stress tests
const save = () => {
  const ids = stressTests.filter((test) => test.flaggedDelete).map((test) => test.id)
  if (ids.length == 0) {
    close()
    return
  }
  deleteStressTestsMutation.mutate({ ids }, { onSettled: close })
}

// Flag a stress test for deletion
const flagDelete = (id: number) => {
  const test = stressTests.find((test) => test.id === id)!
  test.flaggedDelete = !test.flaggedDelete
}
</script>

<template>
  <el-dialog v-model="open" :width="600" :title="`All Stress Tests`" :show-close="false" @closed="close">
    <el-divider class="my-0" />
    <p v-if="stressTests.length == 0">There are no stress tests for this miner yet</p>
    <el-container v-else>
      <el-main class="stress-tests">
        <!-- Loading state -->
        <p v-if="stresTestsQueryPending">Loading...</p>

        <!-- Stress test card -->
        <div v-else class="stress-tests__cards">
          <StressTestCard v-for="test in stressTests" :key="test.id" :stress-test="test" @flag-delete="flagDelete" />
        </div>

        <!-- Buttons -->
        <div class="stress-tests__buttons">
          <el-button @click="cancel">Cancel</el-button>
          <el-button type="primary" :loading="isDeletePending" @click="save">Save</el-button>
        </div>
        <p class="stress-tests__warning">
          {{
            amountFlaggedForDeletion && amountFlaggedForDeletion > 0
              ? `${amountFlaggedForDeletion} stress ${amountFlaggedForDeletion == 1 ? 'test' : 'tests'} flagged for deletion`
              : ''
          }}
        </p>
      </el-main>
    </el-container>
  </el-dialog>
</template>

<style scoped lang="scss">
.stress-tests {
  &__cards {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-spacing-lg);
  }

  &__buttons {
    display: flex;
    justify-content: flex-end;
    gap: var(--spacing-spacing-md);
    margin-top: var(--spacing-spacing-lg);
  }

  &__warning {
    color: var(--component-stroke-xanthous);
  }
}
</style>
